import {BOOLEAN, CATALOG_SHEETS, DECIMAL, INTEGER, VAT, VAT_RATES} from "@/helpers/catalogColumns";
import {GROUP_PATH_SEPARATOR} from "@/helpers/catalogWorkbookData";
import {isValidProductName, sanitizeProductName} from "@/helpers/productName";

export const ERROR = 'ERROR';
const WARNING = 'WARNING';

export const EDIT = 'edit';
export const CLEAR = 'clear';
export const SKIP = 'skip';

const LIST_SEPARATOR = ',';
const PATH_SEPARATOR = '>';
const TRUE_WORDS = ['oui', 'yes', 'ja', 'true', 'vrai', 'waar', '1', 'x'];
const FALSE_WORDS = ['non', 'no', 'nee', 'false', 'faux', 'onwaar', '0'];
const COLOR_PATTERN = /^#?[0-9a-f]{6}$/i;
const BARCODE_PATTERN = /^\d+$/;
const BARCODE_COLUMN = 'barcodes';
const NUMBER_PATTERN = /^-?\d+(\.\d+)?$/;
const UNIQUE_COLUMNS = {Groups: 'path', OptionLists: 'name'};
const REQUIRED_SHEET = 'Products';

function isBlank(raw) {
    return raw === null || raw === undefined || String(raw).trim() === '';
}

function normalizePath(text) {
    return String(text)
        .split(PATH_SEPARATOR)
        .map(part => part.trim())
        .filter(Boolean)
        .join(GROUP_PATH_SEPARATOR);
}

function parseNumber(raw) {
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
    const text = String(raw).replace(/\s/g, '').replace(',', '.');
    return NUMBER_PATTERN.test(text) ? Number(text) : null;
}

function parseBoolean(raw) {
    if (typeof raw === 'boolean') return raw;
    const word = String(raw).trim().toLowerCase();
    if (TRUE_WORDS.includes(word)) return true;
    if (FALSE_WORDS.includes(word)) return false;
    return null;
}

function parseMultiple(column, raw, report) {
    return String(raw)
        .split(LIST_SEPARATOR)
        .map(part => part.trim())
        .filter(Boolean)
        .map(part => (column.path ? normalizePath(part) : part))
        .filter(part => {
            if (column.key !== BARCODE_COLUMN) return true;
            if (!BARCODE_PATTERN.test(part)) {
                report('invalidBarcode', WARNING, {value: part});
                return false;
            }
            if (!Number.isSafeInteger(Number(part))) {
                report('barcodeTooLong', WARNING, {value: part});
                return false;
            }
            return true;
        });
}

function parseCell(column, raw, report) {
    if (isBlank(raw)) return null;
    if (column.multiple) return parseMultiple(column, raw, report);
    if (column.path) return normalizePath(raw);

    switch (column.type) {
        case DECIMAL:
        case INTEGER: {
            const number = parseNumber(raw);
            if (number === null) {
                report(column.type === INTEGER ? 'invalidInteger' : 'invalidNumber', ERROR, {value: raw});
                return null;
            }
            if (column.type === INTEGER && !Number.isInteger(number)) {
                report('invalidInteger', ERROR, {value: raw});
                return null;
            }
            if (number < 0) {
                report('negative', ERROR, {value: raw});
                return null;
            }
            return number;
        }
        case VAT: {
            const number = parseNumber(typeof raw === 'string' ? raw.replace('%', '') : raw);
            if (!VAT_RATES.includes(number)) {
                report('invalidVat', ERROR, {value: raw, rates: VAT_RATES.join(', ')});
                return null;
            }
            return number;
        }
        case BOOLEAN: {
            const value = parseBoolean(raw);
            if (value === null) report('invalidBoolean', WARNING, {value: raw});
            return value;
        }
        default: {
            const text = String(raw).trim();
            let valid = true;
            if (column.maxLength && text.length > column.maxLength) {
                report('tooLong', ERROR, {value: text, max: column.maxLength, length: text.length});
                valid = false;
            }
            if (column.productName && !isValidProductName(text)) {
                const suggestion = sanitizeProductName(text);
                report(suggestion ? 'invalidName' : 'invalidNameNoSuggestion', ERROR, suggestion ? {value: text, suggestion} : {value: text});
                valid = false;
            }
            if (!valid) return null;
            if (column.key !== 'color') return text;
            if (!COLOR_PATTERN.test(text)) {
                report('invalidColor', WARNING, {value: text});
                return null;
            }
            return text.startsWith('#') ? text.toUpperCase() : `#${text.toUpperCase()}`;
        }
    }
}

function findDefinition(name) {
    const lower = name.trim().toLowerCase();
    return CATALOG_SHEETS.find(sheet => sheet.name.toLowerCase() === lower);
}

export function isCatalogFile(rawSheets) {
    return rawSheets.some(rawSheet => {
        const definition = findDefinition(rawSheet.name);
        if (!definition) return false;
        if (definition.name !== REQUIRED_SHEET) return true;
        const header = new Set(rawSheet.header.map(key => key.toLowerCase()));
        return definition.columns.filter(column => column.required).every(column => header.has(column.key.toLowerCase()));
    });
}

export function cellKey(sheet, row, column) {
    return `${sheet}|${row}|${column}`;
}

export function rowKey(sheet, row) {
    return `${sheet}|${row}`;
}

function parseSheet(definition, rawSheet, issues, {fixes, skippedRows}) {
    const sheetName = definition.name;
    const headerIndex = new Map(rawSheet.header.map((key, index) => [key.toLowerCase(), index]));
    const known = new Set(definition.columns.map(column => column.key.toLowerCase()));
    const columns = definition.columns.filter(column => !column.exportOnly && headerIndex.has(column.key.toLowerCase()));

    const unknownColumns = rawSheet.header.filter(key => key && !known.has(key.toLowerCase()));
    if (unknownColumns.length) {
        issues.push({sheet: sheetName, code: 'unknownColumns', level: WARNING, params: {columns: unknownColumns.join(', ')}});
    }

    const missing = definition.columns.filter(column => column.required && !headerIndex.has(column.key.toLowerCase()));
    if (missing.length) {
        missing.forEach(column => issues.push({sheet: sheetName, column: column.key, code: 'missingColumn', level: ERROR, params: {column: column.key}}));
        return {total: rawSheet.rows.length, rows: [], unusable: true};
    }

    const uniqueKey = UNIQUE_COLUMNS[sheetName];
    const seen = new Map();
    const seenBarcodes = new Map();

    const rows = rawSheet.rows.map(line => {
        const skipped = !!skippedRows[rowKey(sheetName, line.rowNumber)];
        const rowIssues = [];
        let excluded = skipped;
        const values = {};
        const rawOf = column => {
            const fix = fixes[cellKey(sheetName, line.rowNumber, column.key)];
            return fix !== undefined ? fix : line.cells[headerIndex.get(column.key.toLowerCase())];
        };
        columns.forEach(column => {
            const raw = rawOf(column);
            const report = (code, level, params = {}) => {
                if (level === ERROR) excluded = true;
                rowIssues.push({sheet: sheetName, row: line.rowNumber, column: column.key, code, level, params, raw: isBlank(raw) ? '' : String(raw)});
            };
            const value = parseCell(column, raw, report);
            if (column.required && isBlank(raw)) report('required', ERROR);
            values[column.key] = value;
        });

        if (!skipped && uniqueKey && values[uniqueKey]) {
            const key = values[uniqueKey].toLowerCase();
            if (seen.has(key)) {
                excluded = true;
                rowIssues.push({sheet: sheetName, row: line.rowNumber, column: uniqueKey, code: 'duplicate', level: ERROR, params: {value: values[uniqueKey], row: seen.get(key)}, raw: values[uniqueKey]});
            } else {
                seen.set(key, line.rowNumber);
            }
        }

        const barcodeColumn = columns.find(column => column.key === BARCODE_COLUMN);
        if (!skipped && !excluded && barcodeColumn && values[BARCODE_COLUMN]?.length) {
            const barcodeKey = barcode => String(Number(barcode));
            const duplicate = values[BARCODE_COLUMN].find(barcode => seenBarcodes.has(barcodeKey(barcode)));
            if (duplicate) {
                excluded = true;
                rowIssues.push({sheet: sheetName, row: line.rowNumber, column: BARCODE_COLUMN, code: 'duplicateBarcode', level: ERROR, params: {value: duplicate, row: seenBarcodes.get(barcodeKey(duplicate))}, raw: String(rawOf(barcodeColumn) ?? '').trim()});
            } else {
                values[BARCODE_COLUMN].forEach(barcode => seenBarcodes.set(barcodeKey(barcode), line.rowNumber));
            }
        }

        if (!skipped) issues.push(...rowIssues);
        return {rowNumber: line.rowNumber, values, excluded, skipped};
    });

    return {total: rawSheet.rows.length, rows, unusable: false};
}

export function parseImportSheets(rawSheets, {withOptionLists = false, fixes = {}, skippedRows = {}} = {}) {
    const issues = [];
    const sheets = {};

    rawSheets.forEach(rawSheet => {
        const definition = findDefinition(rawSheet.name);
        if (!definition) {
            issues.push({sheet: rawSheet.name, code: 'unknownSheet', level: WARNING, params: {sheet: rawSheet.name}});
            return;
        }
        if (definition.optionListsOnly && !withOptionLists) {
            issues.push({sheet: definition.name, code: 'optionListsDisabled', level: WARNING, params: {sheet: definition.name}});
            return;
        }
        sheets[definition.name] = parseSheet(definition, rawSheet, issues, {fixes, skippedRows});
    });

    if (!sheets[REQUIRED_SHEET]) {
        issues.unshift({code: 'missingSheet', level: ERROR, params: {sheet: REQUIRED_SHEET}});
    } else if (!sheets[REQUIRED_SHEET].rows.some(row => !row.excluded)) {
        issues.unshift({sheet: REQUIRED_SHEET, code: 'noProducts', level: ERROR, params: {}});
    }

    return {sheets, issues};
}
